'use client';
import React from 'react';
import ImageCollage from './ImageCollage';
import FounderIntro from './FounderIntro';
import VoicesSlider from './VoicesSlider';
import ImageCarousel from '@/components/ui/ImageCarousel';
import { outingImages, perks } from './constants';
import Link from 'next/link';
import GlobalFootprint from '@/components/common/GlobalFootprint';

export default function AboutPage() {
  return (
    <div className="w-full bg-white">
      {/* Main Content */}
      <main className="w-full flex flex-col gap-9 sm:gap-20 lg:gap-[70px] items-center pb-[3rem]">
        {/* Hero Section */}
        <section className="w-full flex flex-col items-center">
          <div className="w-full flex flex-col items-center">
            {/* Hero Title and CTA */}
            <div className="w-full max-w-[1260px] mx-auto px-4 sm:px-6 lg:px-6 flex flex-col gap-3 sm:gap-4 lg:gap-[14px] items-center">
              <div className="max-w-[60.25rem] px-2 sm:px-4 lg:px-[10px] flex justify-center items-center">
                <h1 className="text-lg sm:text-4xl md:text-5xl font-medium font-ibm-plex leading-tight sm:leading-snug lg:leading-[63px] text-center text-black">
                   Let's Build the Future of AI in Chip Manufacturing together!
                </h1>
              </div>
            </div>
            {/* Hero Images Section */}
            <div className="w-full flex flex-col gap-6 sm:gap-8 lg:gap-[30px] items-center mt-6 sm:mt-8 lg:mt-0">
              <div className="relative w-full max-w-[1080px]">
                <ImageCarousel images={outingImages} />
              </div>
            </div>
          </div>
        </section>
        {/* Open Positions */}
        <section className="w-full bg-[#E1EFFE] flex flex-col gap-8 sm:gap-10 lg:gap-[48px] items-center my-9 py-6">
          <div className="max-w-[811px] flex flex-col sm:flex-row justify-center items-center gap-6 px-4 sm:px-0">
            <span className="text-[#212A31] font-ibm-plex text-lg leading-[160%]">
              Imagine working in a place where everything matters — most importantly, You. Get in
              touch at{' '}
              <Link className="underline" href="mailto:careers@sixense.ai">
                careers@sixense.ai
              </Link>
            </span>
            <Link
              className="flex px-[0.75rem] py-[1rem] bg-[#144AEB] justify-center items-center gap-[0.875rem] rounded-[4px] text-white font-ibm-plex text-[0.875rem] leading-[160%] font-medium min-w-max"
              href="https://www.linkedin.com/jobs/search/?currentJobId=4269524679&f_C=14511531&geoId=92000000&origin=COMPANY_PAGE_JOBS_CLUSTER_EXPANSION&originToLandingJobPostings=4269524679%2C4259094682%2C4268537782%2C4259092854"
            >
              View Open Roles
            </Link>
          </div>
        </section>
        {/* About SixSense Section */}
        <section className="w-full flex flex-col gap-8 sm:gap-10 lg:gap-[48px] items-center">
          <div className="px-2 sm:px-4 lg:px-[10px] flex justify-center items-center">
            <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-medium font-ibm-plex leading-tight lg:leading-[52px] text-center text-black">
              Meet The Team behind Sixsense!
            </h2>
          </div>
          <div className="w-full max-w-[1440px] px-4 sm:px-6 lg:px-[90px]">
            <div className="w-full rounded-lg p-4 sm:p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-9 items-center">
                {/* Text Content */}
                <div className="w-full lg:w-[46%] flex flex-col justify-start gap-3">
                  <p className="text-black font-ibm-plex text-[1.875rem] leading-[3rem] text-center sm:text-left">
                    Our Vision
                  </p>
                  <p className="text-base font-normal font-ibm-plex leading-relaxed lg:leading-[32px] text-black text-center sm:text-left">
                    At SixSense, our mission is to give every chip factory a brain. Chips are the
                    foundation of the modern world — powering everything from AI to electric cars.
                    But building them is one of the most demanding feats of human engineering:
                    thousands of steps, hundreds of machines, and no margin for error. We believe
                    the future of manufacturing will not be built by adding more people — it will be
                    built by factories that think. Factories that can see every detail, learn from
                    every signal, and make better decisions in real time.
                    <br />
                    Sixsense began in 2018 with the vision to bring such trustworthy, intelligent
                    automation to one of the world's most complex industries — semiconductor
                    manufacturing.  Our AI platform transforms raw production data into intelligence
                    — catching hidden issues early, improving yield, and unlocking a new level of
                    control that was once impossible.
                  </p>
                </div>
                {/* Images */}
                <div className="w-full lg:flex-1 flex flex-col items-center">
                  <ImageCollage />
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Meet the Founders Section */}
        <section className="w-full px-4 sm:px-6 lg:px-[90px] flex flex-col gap-8 sm:gap-10 lg:gap-[48px] items-center">
          <FounderIntro />
        </section>
        {/* Voices of Sixsense Section */}
        <section className="w-full flex flex-col gap-2 sm:gap-4 lg:gap-[20px] items-center">
          <h2 className="text-2xl sm:text-3xl lg:text-[40px] font-medium font-ibm-plex leading-tight lg:leading-[52px] text-center text-black">
            Voices of Sixsense
          </h2>
          <div className="w-full">
            <VoicesSlider />
          </div>
        </section>
        {/* Perks of Working at Sixsense Section */}
        <section className="w-full max-w-full bg-[#fafafb] py-6 sm:py-8 md:py-10 px-4 sm:px-12 md:px-18 lg:px-[4.5rem]">
          <div className="w-full max-w-[1260px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-8 sm:gap-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium font-ibm-plex text-center pt-16 text-black px-2 sm:px-4 sm:text-left">
                Perks of Working at Sixsense
              </h2>
              {/* Impact Cards */}
              <div className="w-full flex flex-col gap-2 sm:gap-3">
                {/* Card template*/}
                {perks.map((perk) => (
                  <div
                    className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8 p-6 sm:p-8"
                    key={`perk_${perk.id}`}
                  >
                    <div className="flex-shrink-0 w-20 sm:w-24 h-20 sm:h-24 bg-white border border-[#d7e7ff] rounded-lg flex items-center justify-center p-6 sm:p-7">
                      <img
                        src={perk.image}
                        alt={`badge_${perk.id}`}
                        className="w-12 sm:w-16 h-8 sm:h-12"
                      />
                    </div>
                    <div className="flex flex-col gap-4 sm:gap-5">
                      <h3 className="text-2xl sm:text-3xl font-semibold font-ibm-plex text-black text-center sm:text-left">
                        {perk.title}
                      </h3>
                      <p className="text-md sm:text-[1rem] font-normal font-ibm-plex text-black leading-relaxed text-center sm:text-left">
                        {perk.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <GlobalFootprint />
      </main>
    </div>
  );
}
